/**
 * The numbers and names the garden-side screens are tuned by: beds, the photo
 * tray, seed packet scans, share pages, the gallery, billing, garden members,
 * the profile, and the app shell.
 *
 * Kept in one place, out of the components, so that each can be found and
 * changed without reading the screen that uses it, and so that they can later
 * come from the database instead (each carries its kept-values group, scripts/kept-values.md). Constants
 * only: no functions.
 *
 * Used by: some thirty-five files across features/billing, gallery,
 * locations, packets, photo-tray, share and season, shared/api/gardens,
 * shared/context/LocationsContext, and the profile screens in src/user.
 *
 * @module tunables/garden
 */

// ---------------------------------------------------------------------------
// Locations (beds)
// ---------------------------------------------------------------------------

/**
 * The localStorage key the locations page remembers its density under.
 * @kept K1
 */
export const LOCATION_VIEW_STORAGE_KEY = 'bloom.locationView';

/**
 * The React Query key the archived beds are cached under. LocationsContext
 * (src/shared/context) invalidates the same key by its literal.
 * @kept K1
 */
export const ARCHIVED_LOCATIONS_QUERY_KEY = 'archived-locations';

/**
 * How long a bottom panel waits, after opening, before it focuses its text
 * box: long enough for the panel to slide in, so the phone keyboard rising
 * does not fight the animation. Milliseconds.
 * @kept K5
 */
export const PANEL_FOCUS_DELAY_MS = 250;

/**
 * How long a toast that can be undone (a note on every plant)
 * stays up: the window the gardener has to press Undo. Milliseconds.
 * The plant list's own undo toasts use the same 10 seconds by their literal.
 * @kept K5
 */
export const UNDO_TOAST_MS = 10_000;



/**
 * The bed dialog counts down the characters left in a description once this
 * few remain, so the count reads as a warning rather than decoration.
 * Characters.
 * @kept K5
 */
export const LOCATION_DESCRIPTION_COUNTER_FROM = 100;

/**
 * The width from which plant and location cards sit side by side, so their
 * Notes and Plants rows stay open instead of folding. A CSS media query; the
 * plant list's PlantCard and PlantFilterSheet use the same 768px by literal.
 * @kept K5
 */
export const CARD_MULTI_COLUMN_MEDIA_QUERY = '(min-width: 768px)';

// ---------------------------------------------------------------------------
// Sharing and public pages
// ---------------------------------------------------------------------------

/**
 * The React Query key a location's sharing state (public or not, how often
 * its links were opened) is cached under, followed by the location's id.
 * SharePlantDialog (plant-list) uses the same key by its literal.
 * @kept K1
 */
export const LOCATION_SHARING_QUERY_KEY = 'location-sharing';

/**
 * The React Query key a plant's sharing state is cached under, followed by
 * the plant's id. SharePlantDialog (plant-list) uses the same key by its
 * literal.
 * @kept K1
 */
export const PLANT_SHARING_QUERY_KEY = 'plant-sharing';

/**
 * How long a plant's or location's sharing state is taken as fresh before it
 * is asked for again. Milliseconds.
 * @kept K1
 */
export const SHARING_STALE_MS = 60_000;

// ---------------------------------------------------------------------------
// The photo tray
// ---------------------------------------------------------------------------

/**
 * How long the tray's list of waiting photos is taken as fresh before it is
 * asked for again. Milliseconds.
 * @kept K1
 */
export const TRAY_STALE_MS = 60_000;

/**
 * How many tray photos are shrunk and sent at once: enough to keep the
 * connection busy, not enough to drown a phone. A count of uploads.
 * @kept K4
 */
export const TRAY_UPLOAD_CONCURRENCY = 2;

/**
 * How long a tray upload that got no answer (a timeout, a dropped connection,
 * or the platform's 502/503/504 while the server restarts) waits before it is
 * sent the one more time (B82, features/photo-tray/lib/trayRetry): long enough
 * for a dropped connection to come back, short enough that the gardener
 * barely notices. Milliseconds.
 * @kept K1
 */
export const TRAY_UPLOAD_RETRY_WAIT_MS = 3_000;

/**
 * How long the "Moved" toast stays up after a photo is moved between piles:
 * the window for its Undo. Milliseconds.
 * @kept K5
 */
export const TRAY_MOVE_UNDO_TOAST_MS = 8_000;

// ---------------------------------------------------------------------------
// Seed packet scans
// ---------------------------------------------------------------------------


// ---------------------------------------------------------------------------
// Sharing moments (the first-photo question, the add-photo offer)
// ---------------------------------------------------------------------------

/**
 * The localStorage key holding the plant waiting to be asked about sharing,
 * when its first photo arrived on another screen than the Plants page
 * (plumbing between screens; whether to ask, and the answer, are the
 * server's first_share advice and its marks).
 * @kept K1
 */
export const FIRST_SHARE_PENDING_STORAGE_KEY = 'grow.firstSharePending';

/**
 * The window event announcing a plant's first photo, its detail the plant's
 * id; the Plants page's ShareMoments answers it with the share question.
 * @kept K1
 */
export const FIRST_SHARE_EVENT = 'grow:first-share';

/**
 * The window event asking for the photo screen for a plant, its detail the
 * plant's id (the add form's "Add photo", an empty share sheet).
 * @kept K1
 */
export const ADD_PHOTO_EVENT = 'grow:add-photo';

// ---------------------------------------------------------------------------
// The gallery
// ---------------------------------------------------------------------------

/**
 * How long the gardener's own gallery state is taken as fresh before it is
 * asked for again. Milliseconds.
 * @kept K1
 */
export const GALLERY_STALE_MS = 5 * 60 * 1000;


/**
 * The React Query key the approval queue is cached under, followed by the
 * tab's status; invalidated whole after an approve or hide.
 * @kept K1
 */
export const GALLERY_QUEUE_QUERY_KEY = ['gallery', 'queue'] as const;

// ---------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------

/**
 * How long the billing status is taken as fresh before it is asked for
 * again. It changes when the gardener pays, and the pages where that happens
 * refetch it themselves. Milliseconds.
 * @kept K1
 */
export const BILLING_STALE_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// The profile
// ---------------------------------------------------------------------------

/**
 * How long a setting's "Saved" shows after a change is kept, on profile
 * screens that save as they go. Milliseconds.
 * @kept K5
 */
export const SAVED_TICK_MS = 1800;


/**
 * The React Query key the gardener's own public garden page (its code and
 * link) is cached under, on Profile > Public pages.
 * @kept K1
 */
export const MY_GARDEN_QUERY_KEY = 'my-garden';

/**
 * How long the garden page's link is taken as fresh before it is asked for
 * again. Milliseconds.
 * @kept K1
 */
export const MY_GARDEN_STALE_MS = 5 * 60 * 1000;

/**
 * How long typing must pause before Profile > Public pages asks whether a
 * garden address is free, so a gardener typing "margaret" asks once, not
 * eight times. Milliseconds.
 * @kept K5
 */
export const GARDEN_ADDRESS_CHECK_DELAY_MS = 400;

/**
 * The React Query key the gardener's note shortcuts are cached under (the
 * profile's list and Labels screen). The plant list's QuickNoteChips reads
 * the same key by its literal.
 * @kept K1
 */
export const NOTE_SHORTCUTS_QUERY_KEY = 'noteShortcuts';



/**
 * localStorage: the garden pages (and their plant and bed pages) this
 * browser has opened, so a visit is counted once per browser rather than once
 * per load, as share pages are (gardens, SEEN_SHARES_STORAGE_KEY).
 * @kept K1
 */
export const SEEN_GARDENS_STORAGE_KEY = 'grow.seenGardens';

/**
 * How many opened garden pages a browser remembers before forgetting the
 * oldest (gardens). Past it, a very old page may be counted again.
 * @kept K1
 */
export const SEEN_GARDENS_MAX = 200;

/**
 * The screen width, in CSS pixels, from which a plant's, bed's or share
 * link's own page draws its photos from the large copy (about 1600px; D53,
 * owner 2026-10-15: "on a desktop the shared images look pretty fuzzy")
 * rather than the 800x600 card. Narrower screens, phones, keep the card, so
 * they stay fast. Tailwind's sm, where those pages' photo frame changes shape.
 * @kept K5
 */
export const GARDEN_LARGE_PHOTO_MIN_WIDTH_PX = 640;

/**
 * How long a typed plant search on Gardens on Grow waits for the typing to
 * pause before it asks (GardensPage).
 * Unit: milliseconds
 * @kept K5
 */
export const GARDENS_SEARCH_DELAY_MS = 350;

/**
 * The fewest characters a plant search on Gardens on Grow is asked with;
 * the backend answers a shorter one with nothing (GardensPage).
 * Unit: characters
 * @kept K5
 */
export const GARDENS_SEARCH_MIN = 2;

/**
 * Where the ZIP a visitor typed for "Near my ZIP" on Gardens on Grow is
 * kept in this browser (GardensPage). A ZIP they typed, never a location; it
 * goes to the server only to order the list, and is kept there nowhere.
 * @kept K1
 */
export const GARDENS_ZIP_STORAGE_KEY = 'grow.gardensZip';
